import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { BarChart, Store } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import type { RegistroResumoModel } from "../../../services/comparativoUnidades"
import { useComparativoUnidadesContext } from "../context/useComparativoUnidadesContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1300px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filtrosGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr auto",
        gap: theme.spacing(2),
        alignItems: "flex-end",
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    botaoPesquisar: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#43a047" },
        whiteSpace: "nowrap",
    },
    botaoExportar: {
        borderRadius: 25,
    },
    acoes: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    infoQuantidade: {
        color: "#7f8c8d",
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    chartsGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    barRow: {
        marginBottom: theme.spacing(1.5),
    },
    barLabel: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 4,
    },
    barLabelNome: {
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "#2c3e50",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "60%",
    },
    barLabelValor: {
        fontSize: "0.8rem",
        color: "#7f8c8d",
        whiteSpace: "nowrap",
    },
    barTrack: {
        height: 10,
        backgroundColor: "#e9ecef",
        borderRadius: 5,
        overflow: "hidden",
    },
    barFill: {
        height: "100%",
        backgroundColor: "#4caf50",
        borderRadius: 5,
        transition: "width 0.4s ease",
    },
    semResultados: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(4),
    },
}))

function fmt(val: number): string {
    return val.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function hoje(): string {
    return new Date().toISOString().slice(0, 10)
}

function primeiroDiaMes(): string {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`
}

export function ComponentePrincipal() {
    const classes = useStyles()
    const { dados, erroCarregamento, pesquisar } = useComparativoUnidadesContext()
    const { enqueueSnackbar } = useSnackbar()

    const [dataInicio, setDataInicio] = useState(primeiroDiaMes())
    const [dataFim, setDataFim] = useState(hoje())

    const alertStart = (msg: string, variant: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handlePesquisar = async () => {
        if (!dataInicio || !dataFim) {
            alertStart("Preencha as datas de início e fim.", "error")
            return
        }
        if (dataInicio > dataFim) {
            alertStart("A data de início não pode ser maior que a data fim.", "error")
            return
        }
        await pesquisar(dataInicio, dataFim)
    }

    const handleExportar = () => {
        const registros = dados.registros
        if (registros.length === 0) return
        const comissaoOnlinePct = registros[0].comissaoOnline
        const cabecalho = [
            "ID", "Nome", "RegistrosFidelidade", "QuantidadeResgates",
            "AvaliacaoLoja_Em5", "QuantidadePedidos", "FaturamentoDelivery",
            "TotalFretes", "ClientesNoPeriodo", "NPSDelivery",
            "VendasCartao", `TxCartao ${fmt(comissaoOnlinePct)}%`,
            "VendasPix", "TxPix 2%", "Chargebacks", "SaldoOnline",
        ]
        const linhas = [
            cabecalho.join(";"),
            ...registros.map((r: RegistroResumoModel) => [
                r.id,
                r.nome,
                r.qtdRegistros,
                r.qtdResgatesEfetuados,
                fmt(r.mediaAvaliacoesGeral),
                r.qtdPedidos,
                fmt(r.valorVendas),
                fmt(r.valorFretes),
                r.qtdClientes,
                fmt(r.mediaAvaliacoesDelivery),
                fmt(r.valorVendasOnline),
                fmt(r.comissaoVendasOnline),
                fmt(r.valorVendasOnlinePix),
                fmt(r.comissaoVendasOnlinePix),
                fmt(r.chargebacks),
                fmt(r.saldoOnline),
            ].join(";")),
        ]
        const csv = linhas.join("\n")
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", "dadoscomparativo.csv")
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const totalRegistros = dados.registros.reduce((s, r) => s + r.qtdRegistros, 0)
    const totalVendas = dados.registros.reduce((s, r) => s + r.valorVendas, 0)

    const comparativoFidelidade = [...dados.registros]
        .sort((a, b) => b.qtdRegistros - a.qtdRegistros)
        .map(r => ({
            nome: r.nome,
            valor: r.qtdRegistros,
            pct: totalRegistros > 0 ? (r.qtdRegistros / totalRegistros) * 100 : 0,
        }))

    const comparativoPedidos = [...dados.registros]
        .sort((a, b) => b.valorVendas - a.valorVendas)
        .map(r => ({
            nome: r.nome,
            valor: r.valorVendas,
            pct: totalVendas > 0 ? (r.valorVendas / totalVendas) * 100 : 0,
        }))

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                {/* Filtros */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <BarChart className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtros de Pesquisa
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filtrosGrid}>
                            <TextField
                                label="Data Início"
                                type="date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Data Fim"
                                type="date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dataFim}
                                onChange={e => setDataFim(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button
                                variant="contained"
                                className={classes.botaoPesquisar}
                                onClick={handlePesquisar}
                            >
                                Pesquisar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Tabela */}
                {dados.pesquisado && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Store className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Visão Geral das Lojas
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Box className={classes.acoes}>
                                <Typography variant="body2" className={classes.infoQuantidade}>
                                    Exibindo {dados.registros.length} estabelecimento(s).
                                </Typography>
                                {dados.registros.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        className={classes.botaoExportar}
                                        onClick={handleExportar}
                                    >
                                        Baixar CSV
                                    </Button>
                                )}
                            </Box>
                            {dados.registros.length === 0 ? (
                                <Typography className={classes.semResultados}>
                                    Não existem registros no período.
                                </Typography>
                            ) : (
                                <TableContainer style={{ overflowX: "auto" }}>
                                    <Table size="small" stickyHeader style={{ minWidth: 1100 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: 50 }}>ID</TableCell>
                                                <TableCell style={{ minWidth: 130 }}>Nome</TableCell>
                                                <TableCell align="right" style={{ minWidth: 90 }}>Reg. Fid.</TableCell>
                                                <TableCell align="right" style={{ minWidth: 80 }}>Resgates</TableCell>
                                                <TableCell align="right" style={{ minWidth: 80 }}>Pedidos</TableCell>
                                                <TableCell align="right" style={{ minWidth: 100 }}>Vendas</TableCell>
                                                <TableCell align="right" style={{ minWidth: 110 }}>Online Cartão</TableCell>
                                                <TableCell align="right" style={{ minWidth: 100 }}>Online Pix</TableCell>
                                                <TableCell align="right" style={{ minWidth: 90 }}>Fretes</TableCell>
                                                <TableCell align="right" style={{ minWidth: 80 }}>Clientes</TableCell>
                                                <TableCell align="right" style={{ minWidth: 90 }}>NPS Delivery</TableCell>
                                                <TableCell align="right" style={{ minWidth: 100 }}>Mensalidade</TableCell>
                                                <TableCell align="right" style={{ minWidth: 90 }}>Tx. Cartão %</TableCell>
                                                <TableCell align="right" style={{ minWidth: 100 }}>Saldo Online</TableCell>
                                                <TableCell align="right" style={{ minWidth: 110 }}>Chargebacks</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dados.registros.map(r => {
                                                const cbPct =
                                                    r.valorVendasOnline + r.chargebacks > 0
                                                        ? (r.chargebacks / (r.valorVendasOnline + r.chargebacks)) * 100
                                                        : 0
                                                return (
                                                    <TableRow key={r.id}>
                                                        <TableCell>{r.id}</TableCell>
                                                        <TableCell
                                                            className={classes.nomeCell}
                                                            title={r.nome}
                                                            style={{ maxWidth: 130 }}
                                                        >
                                                            {r.nome}
                                                        </TableCell>
                                                        <TableCell align="right">{r.qtdRegistros}</TableCell>
                                                        <TableCell align="right">{r.qtdResgatesEfetuados}</TableCell>
                                                        <TableCell align="right">{r.qtdPedidos}</TableCell>
                                                        <TableCell align="right">{fmt(r.valorVendas)}</TableCell>
                                                        <TableCell align="right">{fmt(r.valorVendasOnline)}</TableCell>
                                                        <TableCell align="right">{fmt(r.valorVendasOnlinePix)}</TableCell>
                                                        <TableCell align="right">{fmt(r.valorFretes)}</TableCell>
                                                        <TableCell align="right">{r.qtdClientes}</TableCell>
                                                        <TableCell align="right">{fmt(r.mediaAvaliacoesDelivery)}</TableCell>
                                                        <TableCell align="right">{fmt(r.mensalidade)}</TableCell>
                                                        <TableCell align="right">{fmt(r.comissaoOnline)}</TableCell>
                                                        <TableCell align="right">{fmt(r.saldoOnline)}</TableCell>
                                                        <TableCell align="right">
                                                            {fmt(r.chargebacks)}
                                                            <br />
                                                            <span style={{ color: "#7f8c8d", fontSize: "0.75rem" }}>
                                                                ({fmt(cbPct)}%)
                                                            </span>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Paper>
                )}

                {/* Gráficos de barra */}
                {dados.pesquisado && dados.registros.length > 0 && (
                    <Box className={classes.chartsGrid}>
                        <Paper className={classes.configCard}>
                            <Box className={classes.cardHeader}>
                                <BarChart className={classes.iconHeader} />
                                <Typography variant="h6" className={classes.cardHeaderTitle}>
                                    Registros de Fidelidade
                                </Typography>
                            </Box>
                            <Box className={classes.cardContent}>
                                {comparativoFidelidade.map((item, idx) => (
                                    <Box key={idx} className={classes.barRow}>
                                        <Box className={classes.barLabel}>
                                            <span className={classes.barLabelNome} title={item.nome}>
                                                {item.nome}
                                            </span>
                                            <span className={classes.barLabelValor}>{item.valor}</span>
                                        </Box>
                                        <Box className={classes.barTrack}>
                                            <Box
                                                className={classes.barFill}
                                                style={{ width: `${Math.min(item.pct, 100)}%` }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>

                        <Paper className={classes.configCard}>
                            <Box className={classes.cardHeader}>
                                <BarChart className={classes.iconHeader} />
                                <Typography variant="h6" className={classes.cardHeaderTitle}>
                                    Vendas no Delivery
                                </Typography>
                            </Box>
                            <Box className={classes.cardContent}>
                                {comparativoPedidos.map((item, idx) => (
                                    <Box key={idx} className={classes.barRow}>
                                        <Box className={classes.barLabel}>
                                            <span className={classes.barLabelNome} title={item.nome}>
                                                {item.nome}
                                            </span>
                                            <span className={classes.barLabelValor}>
                                                R$ {fmt(item.valor)}
                                            </span>
                                        </Box>
                                        <Box className={classes.barTrack}>
                                            <Box
                                                className={classes.barFill}
                                                style={{ width: `${Math.min(item.pct, 100)}%` }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
